import type { PermissionResponse } from '@api/generated/types.gen';

/** Группа прав, объединённых по «области» — префиксу имени до первой точки. */
export type PermissionGroup = {
  area: string;
  permissions: PermissionResponse[];
};

/**
 * Группирует права по области (префикс имени до первой точки: `users.read` → `users`).
 *
 * Области и права внутри них сортируются по алфавиту. Область выводится из самих
 * данных, а не из захардкоженного списка, поэтому новые области с бэка появляются
 * в UI автоматически.
 *
 * @param permissions справочник прав из `GET /api/admin/permissions`
 * @returns отсортированный список групп
 */
export function groupPermissionsByArea(permissions: PermissionResponse[]): PermissionGroup[] {
  const byArea = new Map<string, PermissionResponse[]>();
  for (const permission of permissions) {
    const name = permission.name ?? '';
    const dot = name.indexOf('.');
    const area = dot === -1 ? name : name.slice(0, dot);
    const bucket = byArea.get(area) ?? [];
    bucket.push(permission);
    byArea.set(area, bucket);
  }
  return [...byArea.entries()]
    .map(([area, perms]) => ({
      area,
      permissions: perms.slice().sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '')),
    }))
    .sort((a, b) => a.area.localeCompare(b.area));
}

/**
 * Сопоставляет имена прав роли с их идентификаторами из справочника.
 *
 * `RoleResponse.permissions` отдаёт имена, а эндпоинты записи принимают id —
 * этот хелпер переводит первое во второе, отбрасывая имена, которых нет в
 * справочнике.
 *
 * @param names   имена прав роли
 * @param catalog справочник прав (с id и name)
 * @returns идентификаторы найденных прав
 */
export function permissionNamesToIds(names: string[], catalog: PermissionResponse[]): string[] {
  const idByName = new Map(catalog.map((p) => [p.name ?? '', p.id ?? '']));
  return names.map((name) => idByName.get(name)).filter((id): id is string => Boolean(id));
}
